import { inject, injectable } from "tsyringe";
import { type ISecureServerCalls, TOKEN_ISecureServerCalls } from "./SecureServerCalls";
import {
    ServerEvent,
    ServerEventBatch,
    EventPlaneDBClient,
    WriteResponse,
    ShareResponse,
    AccessLevel,
    ErrorForbidden,
    ErrorNotExist
} from "@utilitydelta/eventplanedb-client";
import { ProjectEventType } from "@/ProjectWorld";

export { ErrorForbidden, ErrorNotExist, AccessLevel };

export const TOKEN_IServerCalls = Symbol('IServerCalls');

export interface IServerCalls {
    share(projectId: string, accessLevel: AccessLevel, singleUse: boolean, expiresOn: number): Promise<ShareResponse>;
    disableShare(projectId: string, shareId: string): Promise<void>;
    disableUser(projectId: string, userId: string): Promise<void>;
    disableClient(projectId: string, clientId: string): Promise<void>;
    readEvents(projectId: string, fromEventBatchIndex: number, shareId: string | undefined, includeOwnEvents: boolean): Promise<ServerEventBatch<ProjectEventType>[]>;
    writeEvents(projectId: string, createIfNotExist: boolean, clientLastEventBatchIndex: number | undefined, eventsNotOnServer: ServerEvent<ProjectEventType>[]): Promise<WriteResponse>;
    deleteProject(projectId: string): Promise<void>;
}

@injectable()
export class ServerCalls implements IServerCalls {

    constructor(
        @inject(TOKEN_ISecureServerCalls) private secureServerCalls: ISecureServerCalls
    ) { }

    public deleteProject = async (projectId: string): Promise<void> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        await EventPlaneDBClient.deleteProject(baseUrl, authData, projectId);
    }

    public share = async (
        projectId: string,
        accessLevel: AccessLevel,
        singleUse: boolean,
        expiresOn: number
    ): Promise<ShareResponse> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.share(
            baseUrl,
            authData,
            projectId,
            accessLevel,
            singleUse,
            expiresOn
        );
    }

    public disableShare = async (
        projectId: string,
        shareIdHash: string
    ): Promise<void> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.disableShare(
            baseUrl,
            authData,
            projectId,
            shareIdHash
        );
    }

    public disableClient = async (
        projectId: string,
        clientIdHash: string
    ): Promise<void> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.disableClient(
            baseUrl,
            authData,
            projectId,
            clientIdHash
        );
    }

    public disableUser = async (
        projectId: string,
        userIdHash: string
    ): Promise<void> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.disableUser(
            baseUrl,
            authData,
            projectId,
            userIdHash
        );
    }

    public readEvents = async (
        projectId: string,
        fromEventBatchIndex: number,
        shareIdHash: string | undefined,
        includeOwnEvents: boolean
    ): Promise<ServerEventBatch<ProjectEventType>[]> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.readEvents<ProjectEventType>(
            baseUrl,
            authData,
            projectId,
            fromEventBatchIndex,
            shareIdHash,
            includeOwnEvents
        );
    }

    public writeEvents = async (
        projectId: string,
        createIfNotExist: boolean,
        clientLastEventBatchIndex: number | undefined,
        eventsNotOnServer: ServerEvent<ProjectEventType>[]
    ): Promise<WriteResponse> => {
        const baseUrl = await this.secureServerCalls.getApiBaseUrl(projectId);
        const authData = await this.secureServerCalls.AuthData();

        return await EventPlaneDBClient.writeEvents<ProjectEventType>(
            baseUrl,
            authData,
            projectId,
            createIfNotExist,
            clientLastEventBatchIndex,
            eventsNotOnServer
        );
    }
}